import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import { ModelSelection, ModelType } from '../types/models';
import { ArtifactService } from '../services/api/artifactService';

export type ChatUIRole = 'architect' | 'operator' | 'coach' | 'integrator' | 'development' | 'marketfit';

interface ChatUIContextType {
  // Model selection for ChatUI
  model: ModelSelection | null;
  setModel: (model: ModelSelection | null) => void;
  
  // UI Role for ChatUI
  uiRole: ChatUIRole;
  setUIRole: (role: ChatUIRole) => void;
  
  // Loading state
  isLoading: boolean;
  
  // Initialize with defaults
  initializeDefaults: () => Promise<void>;
}

const ChatUIContext = createContext<ChatUIContextType | undefined>(undefined);

export const useChatUI = () => {
  const context = useContext(ChatUIContext);
  if (!context) {
    throw new Error('useChatUI must be used within a ChatUIProvider');
  }
  return context;
};

interface ChatUIProviderProps {
  children: React.ReactNode;
}

export const ChatUIProvider: React.FC<ChatUIProviderProps> = ({ children }) => {
  const { selectedCompany, availableCompanies, user } = useUser();
  const [model, setModel] = useState<ModelSelection | null>(null);
  const [uiRole, setUIRole] = useState<ChatUIRole>('operator');
  const [isLoading, setIsLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Track the last company ID to detect company changes
  const lastCompanyIdRef = React.useRef<string | undefined>(undefined);
  const initializingRef = React.useRef(false);
  const initGenerationRef = React.useRef(0);

  // Rebind ChatUI model to the selected company's artifact tree (preserve uiRole)
  const initializeDefaults = useCallback(async () => {
    const generation = ++initGenerationRef.current;
    initializingRef.current = true;
    setIsLoading(true);
    try {
      if (selectedCompany?.id) {
        const accessibleCompanyIds = availableCompanies.map(uc => uc.company.id);
        const trees = await ArtifactService.getTrees(selectedCompany.id, accessibleCompanyIds);
        if (generation !== initGenerationRef.current) return;

        if (trees.length > 0) {
          const defaultTree = trees[0];
          const defaultModel: ModelSelection = {
            type: ModelType.COMPANY,
            artifactTreeId: defaultTree.id,
          };
          setModel(defaultModel);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('🎯 [ChatUIContext] Initialized with default model:', JSON.stringify({
              type: defaultModel.type,
              companyId: selectedCompany.id,
              artifactTreeId: defaultTree.id,
              treeName: defaultTree.name,
            }, null, 2));
          }
        } else {
          setModel(null);
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ [ChatUIContext] No artifact trees found for selected company:', selectedCompany.id);
          }
        }
      } else {
        setModel(null);
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ [ChatUIContext] No selected company, model set to null');
        }
      }
      
      setInitialized(true);
    } catch (error) {
      if (generation !== initGenerationRef.current) return;
      console.error('❌ [ChatUIContext] Failed to initialize defaults:', error);
      setModel(null);
      setInitialized(true);
    } finally {
      if (generation === initGenerationRef.current) {
        setIsLoading(false);
        initializingRef.current = false;
      }
    }
  }, [selectedCompany, availableCompanies]);

  // Initialize / rebind when user or header company changes
  useEffect(() => {
    if (!user) {
      setModel(null);
      setUIRole('operator');
      setInitialized(false);
      setIsLoading(false);
      lastCompanyIdRef.current = undefined;
      initializingRef.current = false;
      return;
    }

    if (!selectedCompany?.id) {
      setModel(null);
      setInitialized(false);
      setIsLoading(false);
      lastCompanyIdRef.current = undefined;
      return;
    }

    if (lastCompanyIdRef.current === selectedCompany.id && initialized) {
      return;
    }

    const companyChanged = lastCompanyIdRef.current !== selectedCompany.id;
    lastCompanyIdRef.current = selectedCompany.id;
    if (companyChanged) {
      try {
        localStorage.removeItem('chatUI_model');
      } catch {
        /* ignore */
      }
      setModel(null);
    }
    void initializeDefaults();
  }, [user, selectedCompany?.id, initializeDefaults, initialized]);

  // Persist model and role to localStorage (optional)
  useEffect(() => {
    if (initialized && model && selectedCompany?.id) {
      try {
        localStorage.setItem(
          'chatUI_model',
          JSON.stringify({ ...model, companyId: selectedCompany.id })
        );
      } catch (error) {
        console.error('Failed to persist ChatUI model:', error);
      }
    }
  }, [model, initialized, selectedCompany?.id]);

  useEffect(() => {
    if (initialized) {
      try {
        localStorage.setItem('chatUI_uiRole', uiRole);
      } catch (error) {
        console.error('Failed to persist ChatUI uiRole:', error);
      }
    }
  }, [uiRole, initialized]);

  // Restore role only; model is always rebound from the selected company's trees
  useEffect(() => {
    if (!selectedCompany?.id || initialized) return;
    try {
      const persistedRole = localStorage.getItem('chatUI_uiRole') as ChatUIRole | null;
      if (
        persistedRole &&
        ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'].includes(
          persistedRole
        )
      ) {
        setUIRole(persistedRole);
      }
    } catch (error) {
      console.error('Failed to load persisted ChatUI uiRole:', error);
    }
  }, [selectedCompany?.id, initialized]);

  // Wrapper for setModel to add logging
  const setModelWithLogging = useCallback((newModel: ModelSelection | null) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🎯 [ChatUIContext] Model changed:', JSON.stringify({
        type: newModel?.type,
        artifactId: newModel?.artifactId,
        artifactTreeId: newModel?.artifactTreeId,
      }, null, 2));
    }
    setModel(newModel);
  }, []);

  const value: ChatUIContextType = {
    model,
    setModel: setModelWithLogging,
    uiRole,
    setUIRole,
    isLoading,
    initializeDefaults,
  };

  return (
    <ChatUIContext.Provider value={value}>
      {children}
    </ChatUIContext.Provider>
  );
};
